import "swiper/css";
import Icon from "../ui/Icon";
import { Link } from "react-router";
import LazyImage from "../ui/LazyImage";
import CategoryIcon from "../CatIconMapper";
import { useState, useMemo, useRef } from "react";
import { Sheet, SheetContent } from "../ui/sheet";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import miniLogo from "../../assets/images/Base-pic.png";
import { toPersianDate } from "../../utils/dateFormatter";
import { highlightText } from "../../utils/TextHighLighter";
import { useCategories } from "../../features/home/hooks/useCategories";

type CategoriesMenuContentProps = {
  categories: any[];
  searchQuery: string;
  onCategoryClick?: () => void;
  closeDropdownMenu?: () => void;
  closeSheetMenu?: () => void;
};

export default function CategoriesMenuContent({
  categories,
  searchQuery,
  onCategoryClick,
  closeDropdownMenu,
  closeSheetMenu,
}: CategoriesMenuContentProps) {
  const [selectedCategory, setSelectedCategory] = useState<any | null>(null);
  const [isMobileDetailsOpen, setIsMobileDetailsOpen] = useState(false);
  const swiperRef = useRef<SwiperType | null>(null);
  const [categoryId, setCategoryId] = useState<number | null>(null);

  const { data: items = [], isLoading } = useCategories(
    categoryId ? { model: "projects", category_id: categoryId } : undefined,
  );

  const displayedCategories = useMemo(() => {
    return categories;
  }, [categories]);

  return (
    <div className="w-full transition-all duration-300 ease-in-out">
      <div className="hidden md:block w-full max-w-[326px] mx-auto bg-complement-01 rounded-[25px] shadow-sm overflow-hidden">
        <Swiper
          slidesPerView={1}
          allowTouchMove={false}
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          className="w-full"
        >
          {/* اسلاید اول: لیست دسته‌بندی‌ها به همراه لوگو و عنوان */}
          <SwiperSlide>
            <div className="flex flex-col items-center gap-y-7 p-6 h-fit">
              <Link
                onClick={() => {
                  setSelectedCategory(null);
                  setCategoryId(null);
                  onCategoryClick?.();
                  closeDropdownMenu?.();
                }}
                to="/founder"
                className="hidden xl:flex flex-col items-center justify-center cursor-pointer"
              >
                <div className="size-[123px] overflow-hidden bg-green-01">
                  <LazyImage
                    src={miniLogo}
                    alt="mini logo"
                    className="object-cover background-home-image"
                  />
                </div>
                <span className="text-center text-green-05">
                  <p className="text-[10px] font-semibold font-kahroba opacity-80">
                    {highlightText("حجة الاسلام و المسلمین", searchQuery)}
                  </p>
                  <h4 className="text-[16px] font-black font-kahroba my-0.5">
                    {highlightText("ســید جــواد شهرســتانی", searchQuery)}
                  </h4>
                  <p className="text-[10px] font-semibold font-kahroba opacity-80">
                    {highlightText(
                      "موسس و مدیر موسسه آل البیت علیهم اسلام",
                      searchQuery,
                    )}
                  </p>
                </span>
              </Link>

              <div className="grid grid-cols-3 gap-2.5 w-full">
                {displayedCategories.map((cat: any) => (
                  <button
                    key={cat.id}
                    className="flex flex-col items-center gap-y-1 text-center focus:outline-none group"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCategory(cat);
                      setCategoryId(cat.id);
                      swiperRef.current?.slideNext();

                      if (onCategoryClick) onCategoryClick();
                    }}
                  >
                    <div
                      className={`size-[60px] flex items-center justify-center shadow-sm border rounded-[22px] transition-all duration-300 ${
                        selectedCategory?.id === cat.id
                          ? "bg-green-05 text-white"
                          : "bg-white group-hover:bg-transparent"
                      }`}
                    >
                      <CategoryIcon
                        id={cat.id}
                        className={`size-5 flex items-center justify-center ${
                          selectedCategory?.id === cat.id
                            ? "bg-green-05 text-white"
                            : "bg-white text-green-05 group-hover:bg-green-01"
                        }`}
                      />
                    </div>
                    <span
                      className={`text-[11px] ${
                        selectedCategory?.id === cat.id
                          ? "text-green-06 font-semibold"
                          : "group-hover:opacity-80 text-green-05"
                      } truncate w-full px-1`}
                      dir="rtl"
                    >
                      {cat.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </SwiperSlide>

          {/* اسلاید دوم: جزئیات دسته‌بندی انتخاب شده (با محافظت ضد کرش) */}
          <SwiperSlide>
            <div
              className="flex flex-col items-center justify-end w-full h-full gap-y-6 px-4 pt-11 overflow-y-auto no-scrollbar"
              onClick={(e) => e.stopPropagation()}
            >
              {selectedCategory && (
                <>
                  <div className="w-full flex items-center justify-between relative my-4 px-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        swiperRef.current?.slidePrev();
                        setTimeout(() => {
                          setSelectedCategory(null);
                          setCategoryId(null);
                        }, 300);
                      }}
                      className="size-9 flex items-center justify-center bg-[#C3D4D2] hover:bg-opacity-80 transition-colors focus:outline-none rounded-full"
                    >
                      <Icon icon="Left1" className="w-4 text-green-04" />
                    </button>

                    <div className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-y-2">
                      <div className="size-20 bg-[#3d6766] flex items-center justify-center rounded-[28px] text-white shadow-sm">
                        <CategoryIcon
                          id={selectedCategory.id}
                          className="size-7"
                        />
                      </div>
                      <span className="text-[14px] font-extrabold text-[#3d6766] font-estedad">
                        {selectedCategory.name}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2.5 w-full max-h-[440px] overflow-y-auto no-scrollbar p-1 mt-4">
                    {isLoading ? (
                      <div className="flex items-center justify-center w-full py-8">
                        <div className="size-6 border-2 border-green-04 border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : items.length > 0 ? (
                      items.map((item: any) => (
                        <Link
                          key={item.id}
                          to={`/institutions/${item.id}`}
                          className="flex items-center justify-end gap-x-2.5 w-full bg-white shadow-sm hover:bg-green-02 transition-colors cursor-pointer p-2.5 rounded-[20px] min-w-0"
                          style={{ textDecoration: "none" }}
                          onClick={() => {
                            setSelectedCategory(null);
                            setCategoryId(null);
                            onCategoryClick?.();
                            closeDropdownMenu?.();
                          }}
                        >
                          <div className="flex flex-col gap-y-1 min-w-0 items-end">
                            <span
                              className="block w-full truncate text-right text-[15px] font-bold text-green-05 font-estedad"
                              dir="rtl"
                            >
                              {item.name}
                            </span>
                            <span
                              className="text-[11px] font-medium text-green-04"
                              dir="rtl"
                            >
                              {item.date_establishment
                                ? toPersianDate(item.date_establishment)
                                : ""}
                            </span>
                          </div>

                          <div className="flex items-center justify-center size-[70px] bg-white border border-complement-02 flex-shrink-0 shadow-sm p-1 rounded-[19px] overflow-hidden">
                            {item.image && (
                              <LazyImage
                                src={item.image || miniLogo}
                                alt={item.name}
                                className="object-cover size-full"
                              />
                            )}
                          </div>
                        </Link>
                      ))
                    ) : (
                      <div className="text-center text-green-04 text-[13px] py-8">
                        موردی یافت نشد
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </SwiperSlide>
        </Swiper>
      </div>

      {/* 📱 موبایل: ساختار اصلی کاملاً دست‌نخورده و بدون تغییر عملکرد */}
      <div className="md:hidden w-full bg-complement-01 flex flex-col items-center gap-y-7 p-4 rounded-[25px]">
        <div className="grid grid-cols-3 gap-2.5 w-full">
          {displayedCategories.map((cat: any) => (
            <button
              key={cat.id}
              className="flex flex-col items-center gap-y-1 text-center focus:outline-none group"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedCategory(cat);
                setCategoryId(cat.id);
                setIsMobileDetailsOpen(true);
                if (onCategoryClick) onCategoryClick();
              }}
            >
              <div className="size-[60px] flex items-center justify-center shadow-sm border bg-white rounded-[22px]">
                <CategoryIcon id={cat.id} className="size-5 text-green-05" />
              </div>
              <span
                className="text-[11px] text-green-05 truncate w-full px-1"
                dir="rtl"
              >
                {cat.name}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* شیت موبایل کامپوننت Shadcn */}
      <Sheet open={isMobileDetailsOpen} onOpenChange={setIsMobileDetailsOpen}>
        <SheetContent
          side="bottom"
          className="md:hidden bg-complement-01 p-5 h-[85vh] overflow-hidden"
        >
          {selectedCategory && (
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between mb-6">
                <div className="w-9" />
                <div className="flex flex-col items-center flex-1">
                  <div className="size-20 bg-[#3d6766] flex items-center justify-center rounded-[28px] text-white shadow-sm">
                    <CategoryIcon id={selectedCategory.id} className="size-7" />
                  </div>
                  <span className="text-[14px] font-extrabold text-[#3d6766] mt-2 font-estedad">
                    {selectedCategory.name}
                  </span>
                </div>

                <button
                  onClick={() => {
                    setIsMobileDetailsOpen(false);
                    setSelectedCategory(null);
                  }}
                  className="size-9 flex items-center justify-center bg-[#C3D4D2] rounded-full"
                >
                  <Icon icon="Left1" className="w-4 text-green-04" />
                </button>
              </div>

              <div
                dir="rtl"
                className="flex flex-col gap-2 flex-1 min-h-0 overflow-y-auto py-4 no-scrollbar"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center w-full h-full">
                    <div className="size-4 border-2 border-green-04 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : items.length > 0 ? (
                  items.map((item: any) => (
                    <Link
                      key={item.id}
                      to={`/institutions/${item.id}`}
                      className="flex items-center justify-end gap-x-2.5 w-full bg-white shadow-sm p-2.5 rounded-[20px]"
                      onClick={() => {
                        setSelectedCategory(null);
                        setIsMobileDetailsOpen(false);
                        setCategoryId(null);
                        onCategoryClick?.();
                        closeSheetMenu?.();
                      }}
                    >
                      <div className="size-[70px] rounded-[19px] overflow-hidden border border-complement-02 flex-shrink-0">
                        {item.image ? (
                          <LazyImage
                            src={item.image}
                            alt={item.name}
                            className="object-cover size-full"
                          />
                        ) : (
                          <div className="flex items-center justify-center size-full text-[9px] text-gray-300">
                            لوگو
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-y-1 min-w-0 items-end flex-1">
                        <span className="block w-full truncate text-right text-[15px] font-bold text-green-05 font-estedad">
                          {item.name}
                        </span>
                        <span className="text-[11px] text-right w-full font-medium text-green-04">
                          {item.date_establishment
                            ? toPersianDate(item.date_establishment)
                            : ""}
                        </span>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="text-center py-10">موردی یافت نشد</div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
